import React from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AiAmrOverviewMetric, AiAmrSummary } from "./aiAmrSummaryTypes";
import AiSummaryBiologicalAgeCard from "./AiSummaryBiologicalAgeCard";
import AiSummaryRecommendationsView, {
  hasAiSummaryRecommendations,
} from "./AiSummaryRecommendationsView";
import AiAmrZonePanels from "./AiAmrZonePanels";
import { AMR_METRIC_GROUP_LABELS } from "./aiAmrSummaryPresentation";
import classes from "./AiRmrSummary.module.css";
import amrClasses from "./AiAmrSummaryView.module.css";

type Props = {
  summary: AiAmrSummary;
};

type MetricTone = "danger" | "warning" | "neutral" | "success";

const clampPercent = (value: number): number =>
  Math.max(0, Math.min(100, value));

const formatNumber = (value: number): string =>
  new Intl.NumberFormat(undefined, {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);

const formatDate = (value: string): string => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const getMetricTone = (scoreCategory?: number): MetricTone => {
  if (scoreCategory === 1) {
    return "danger";
  }
  if (scoreCategory === 2) {
    return "warning";
  }
  if (scoreCategory === 4 || scoreCategory === 5) {
    return "success";
  }
  return "neutral";
};

const metricToneClass: Record<MetricTone, string> = {
  danger: classes.metricToneDanger,
  warning: classes.metricToneWarning,
  neutral: classes.metricToneNeutral,
  success: classes.metricToneSuccess,
};

const OverviewMetricCard = ({ metric }: { metric: AiAmrOverviewMetric }) => {
  const tone = getMetricTone(metric.scoreCategory);
  const progress = clampPercent(metric.value);

  return (
    <article className={`${classes.metricCard} ${metricToneClass[tone]}`}>
      <div
        className={classes.metricProgress}
        role="progressbar"
        aria-label={`${metric.label} score`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
      >
        <svg viewBox="0 0 44 44" aria-hidden="true">
          <circle
            className={classes.metricProgressTrack}
            cx="22"
            cy="22"
            r="18"
            pathLength="100"
          />
          <circle
            className={classes.metricProgressValue}
            cx="22"
            cy="22"
            r="18"
            pathLength="100"
            strokeDasharray={`${progress} ${100 - progress}`}
          />
        </svg>
        <span>
          {formatNumber(metric.value)}
          {metric.unit || "%"}
        </span>
      </div>
      <div className={classes.metricContent}>
        <p className={classes.metricGroup}>
          {AMR_METRIC_GROUP_LABELS[metric.id] || "Health metric"}
        </p>
        <h4>{metric.label}</h4>
        {metric.scoreCategoryLabel && (
          <span className={classes.metricCategory}>
            {metric.scoreCategoryLabel}
          </span>
        )}
      </div>
    </article>
  );
};

const AiAmrSummaryView = ({ summary }: Props) => {
  const vo2Peak = summary.measuredValues.find(
    (measurement) => measurement.id === "VO2PEAK"
  );
  const hasIdentity = Boolean(
    summary.patient.companyName ||
      summary.legend ||
      summary.patient.name ||
      summary.patient.testDate ||
      summary.reportType
  );
  const hasHero = Boolean(
    vo2Peak ||
      summary.patient.biologicalAge !== undefined ||
      summary.patient.chronologicalAge !== undefined
  );
  const hasRecommendations = hasAiSummaryRecommendations({
    trainingPlan: summary.trainingPlan,
    nutrition: summary.nutrition,
    recommendations: summary.recommendations,
  });
  const hasContent = Boolean(
    hasIdentity ||
      hasHero ||
      summary.overviewMetrics.length > 0 ||
      summary.measuredValues.length > 0 ||
      summary.trainingZones.length > 0 ||
      summary.energyConsumption.length > 0 ||
      hasRecommendations ||
      summary.disclaimer
  );

  if (!hasContent) {
    return (
      <section className={classes.emptyState}>
        <h3>No AMR summary content was returned</h3>
        <p>The AI response did not include any displayable AMR fields.</p>
      </section>
    );
  }

  return (
    <div className={classes.report}>
      {hasIdentity && (
        <header className={classes.reportHeader}>
          <div>
            {summary.patient.companyName && (
              <h3>{summary.patient.companyName}</h3>
            )}
            {summary.legend && <p>{summary.legend}</p>}
          </div>
          <div className={classes.patientIdentity}>
            {summary.patient.name && <h3>{summary.patient.name}</h3>}
            {summary.patient.testDate && (
              <p>Test date: {formatDate(summary.patient.testDate)}</p>
            )}
            {summary.reportType && <span>{summary.reportType}</span>}
          </div>
        </header>
      )}

      {hasHero && (
        <section className={amrClasses.heroGrid}>
          <AiSummaryBiologicalAgeCard
            biologicalAge={summary.patient.biologicalAge}
            chronologicalAge={summary.patient.chronologicalAge}
            description={summary.bioAgeDescription}
          />
          {vo2Peak && (
            <article className={`${classes.heroCard} ${amrClasses.vo2Card}`}>
              <p className={classes.cardEyebrow}>VO2 Peak</p>
              <div className={amrClasses.vo2Value}>
                <strong>{formatNumber(vo2Peak.value)}</strong>
                {vo2Peak.unit && <span>{vo2Peak.unit}</span>}
              </div>
              {summary.vo2PeakDescription && (
                <p>{summary.vo2PeakDescription}</p>
              )}
            </article>
          )}
        </section>
      )}

      {summary.overviewMetrics.length > 0 && (
        <section className={classes.reportSection}>
          <h3 className={classes.sectionTitle}>Overview</h3>
          <div className={classes.overviewGrid}>
            {summary.overviewMetrics.map((metric) => (
              <OverviewMetricCard key={metric.id} metric={metric} />
            ))}
          </div>
        </section>
      )}

      {summary.measuredValues.length > 0 && (
        <section className={classes.measuredSection}>
          <h3 className={classes.measuredTitle}>Measured values</h3>
          <div className={classes.measuredGrid}>
            {summary.measuredValues.map((measurement) => (
              <div key={measurement.id} className={classes.measuredValue}>
                <span>{measurement.label}</span>
                <strong>{formatNumber(measurement.value)}</strong>
                {measurement.unit && <small>{measurement.unit}</small>}
              </div>
            ))}
          </div>
        </section>
      )}

      <AiAmrZonePanels
        trainingZones={summary.trainingZones}
        energyConsumption={summary.energyConsumption}
      />

      {hasRecommendations && (
        <AiSummaryRecommendationsView
          trainingPlan={summary.trainingPlan}
          nutrition={summary.nutrition}
          recommendations={summary.recommendations}
        />
      )}

      {summary.disclaimer && (
        <section className={classes.disclaimer}>
          <InfoOutlinedIcon aria-hidden="true" />
          <div>
            <strong>Disclaimer</strong>
            <p>{summary.disclaimer}</p>
          </div>
        </section>
      )}
    </div>
  );
};

export default AiAmrSummaryView;
